import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { DatePicker } from '~/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Skeleton } from '~/components/ui/skeleton';
import { jsDateToIsoDateOnly } from '~/utils/date';

import { useAnalyticsSyncTargets, useTriggerAnalyticsSync } from '../hooks/use-analytics-sync';

interface AnalyticsSyncDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  experimentId: string;
  experimentName: string;
  /** Lower bound of the date picker — when the experiment started running. */
  experimentStartDate: Date;
}

/** Days of lookback the sync API allows (`MAX_ANALYTICS_LOOKBACK_DAYS`). */
const MAX_LOOKBACK_DAYS = 30;

/**
 * Confirm dialog for the "Sync analytics data" action. Lists the connected
 * feeding sources that will be synced and lets the operator pick a From/To
 * window: start = experiment start date, end = today. Start is clamped to the
 * API's 30-day lookback cap so an older experiment can't produce a 422.
 * On confirm it fires one sync per source; the progress toast takes over.
 */
export function AnalyticsSyncDialog({
  open,
  onOpenChange,
  experimentId,
  experimentName,
  experimentStartDate,
}: AnalyticsSyncDialogProps) {
  const { t } = useTranslation('experiments');
  const { targets, isLoading } = useAnalyticsSyncTargets();
  const trigger = useTriggerAnalyticsSync();
  const fromLabelId = useId();
  const toLabelId = useId();

  // Picker bounds: earliest = 30 days before the experiment start date, latest
  // = today. Extending the lower bound back by the lookback window lets the
  // operator sync data leading up to the experiment start.
  const { minDate, maxDate } = useMemo(() => {
    const now = new Date();
    const min = new Date(experimentStartDate);
    min.setDate(min.getDate() - MAX_LOOKBACK_DAYS);
    return { minDate: min, maxDate: now };
  }, [experimentStartDate]);

  // Default the range to the full [min, max] window so the operator can sync
  // the whole experiment span in one click without touching the pickers.
  const [from, setFrom] = useState<Date | undefined>(() => minDate);
  const [to, setTo] = useState<Date | undefined>(() => maxDate);

  const rangeInverted = from && to ? from > to : false;
  const canSync = targets.length > 0 && !!from && !!to && !rangeInverted && !trigger.isPending;

  const handleConfirm = () => {
    if (!from || !to) return;
    trigger.mutate(
      {
        experimentId,
        experimentName,
        targets,
        dateRange: { startDate: jsDateToIsoDateOnly(from), endDate: jsDateToIsoDateOnly(to) },
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          // Reset back to the default full window for the next open.
          setFrom(minDate);
          setTo(maxDate);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('detail.syncAnalytics.dialog.title')}</DialogTitle>
          <DialogDescription>{t('detail.syncAnalytics.dialog.description')}</DialogDescription>
        </DialogHeader>

        {/* Sources that will be synced */}
        <div className="space-y-2">
          <span className="text-foreground text-sm font-medium">
            {t('detail.syncAnalytics.dialog.sourcesLabel')}
          </span>
          {isLoading ? (
            <Skeleton className="h-6 w-full" />
          ) : targets.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {t('detail.syncAnalytics.dialog.noSources')}
            </p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {targets.map((tgt) => (
                <li
                  key={tgt.connectionId}
                  className="bg-selected-row text-foreground inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium"
                >
                  {t(`detail.syncAnalytics.sources.${tgt.code}`, { defaultValue: tgt.name })}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* From / To range */}
        <div className="grid grid-cols-2 gap-3">
          <div role="group" aria-labelledby={fromLabelId} className="space-y-1">
            <span id={fromLabelId} className="text-muted-foreground block text-xs">
              {t('detail.syncAnalytics.dialog.from')}
            </span>
            <DatePicker
              {...(from ? { value: from } : {})}
              onChange={setFrom}
              placeholder={t('detail.syncAnalytics.dialog.datePlaceholder')}
              fromDate={minDate}
              toDate={maxDate}
              className="h-9"
            />
          </div>
          <div role="group" aria-labelledby={toLabelId} className="space-y-1">
            <span id={toLabelId} className="text-muted-foreground block text-xs">
              {t('detail.syncAnalytics.dialog.to')}
            </span>
            <DatePicker
              {...(to ? { value: to } : {})}
              onChange={setTo}
              placeholder={t('detail.syncAnalytics.dialog.datePlaceholder')}
              fromDate={minDate}
              toDate={maxDate}
              className="h-9"
            />
          </div>
        </div>
        {rangeInverted && (
          <p className="text-error text-xs">{t('detail.syncAnalytics.dialog.rangeError')}</p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            className="cursor-pointer"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t('detail.syncAnalytics.dialog.cancel')}
          </Button>
          <Button
            className="cursor-pointer"
            disabled={!canSync}
            isLoading={trigger.isPending}
            onClick={handleConfirm}
          >
            {t('detail.syncAnalytics.dialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
